import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { login, loginWithSocial, resendVerification } from '../api/client';
import { useAuthStore } from '../store/auth';
import Field from '../components/shared/Field';
import { useI18n } from '../i18n';
import { inlineError } from '../hooks/useApiCall';
import { errorCode } from '../lib/httpError';
import { useBusy } from '../hooks/useBusy';
import { usePageTitle } from '../hooks/usePageTitle';
import AuthCard from '../components/auth/AuthCard';
import Notice from '../components/shared/Notice';

export default function Login() {
  const { t } = useI18n();
  usePageTitle(t.nav.login);
  const setAuth = useAuthStore(s => s.setAuth);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('reason') === 'session_expired';
  // #457 — a failed Google sign-in comes back with ?error=<code>. Only the
  // codes of the sign-in are shown (#488 adds ACCOUNT_EXISTS_UNLINKED); any
  // other value (the old `oauth_failed`, or a crafted one such as
  // `__proto__`, which reached Object.prototype through t.errors and crashed
  // the page) shows the generic OAUTH_FAILED text.
  const signInError = (() => {
    const code = searchParams.get('error');
    if (!code) return null;
    if (code === 'EMAIL_NOT_VERIFIED') return t.errors.EMAIL_NOT_VERIFIED;
    if (code === 'ACCOUNT_EXISTS_UNLINKED') return t.errors.ACCOUNT_EXISTS_UNLINKED;
    return t.errors.OAUTH_FAILED;
  })();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  // #605, #664 — the lock of Sign in: a second tap sends no second sign-in.
  const signIn = useBusy();
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  // #605 — the lock of the resend: a second tap sends no second email.
  const resend = useBusy();
  // #661 — the resend worked: the success shows in its own green box, as on Register.
  const [resent, setResent] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await signIn.run(async () => {
      setError(''); setResent(false);
      try {
        const res = await login(form.email, form.password);
        setAuth(res.data.user);
        navigate('/');
      } catch (err) {
        const code = errorCode(err);
        if (code === 'EMAIL_NOT_VERIFIED') setNeedsVerification(true);
        setError(inlineError(err));
      }
    });
  }

  return (
    <AuthCard title={t.home.title} subtitle={t.home.signInToContinue}>

      {sessionExpired && <Notice tone="warning">{t.errors.SESSION_EXPIRED}</Notice>}
      {signInError && <Notice tone="error" data-testid="signin-error">{signInError}</Notice>}

      {/* Social login buttons */}
      <div className="space-y-2">
        <button type="button" onClick={() => loginWithSocial('google')}
          className="btn-outline w-full py-2.5 flex items-center justify-center gap-2">
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
            <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2.04a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
            <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
            <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.31z"/>
          </svg>
          {t.home.continueGoogle}
        </button>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400 uppercase tracking-wide">{t.home.orDivider}</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {/* Expandable email/password login */}
      {!showEmailLogin ? (
        <button type="button" onClick={() => setShowEmailLogin(true)}
          className="btn-outline w-full py-2.5 text-sm">
          {t.home.email} / {t.home.password}
        </button>
      ) : (
        <>

          <form onSubmit={handleSubmit} className="space-y-3">
            <Field label={t.home.email}>
              <input type="email" required className="input" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="login-email" />
            </Field>
            <Field label={t.home.password}>
              <input type="password" required className="input" value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))} data-testid="login-password" />
            </Field>
            {error && <Notice tone="error">{error}</Notice>}
            {error && needsVerification && (
              // The button is a sibling of the alert, not a child. A live region that holds a
              // control announces the label of the control with the text.
              <button type="button" disabled={resend.busy} onClick={() => resend.run(async () => {
                try { await resendVerification(form.email); setError(''); setNeedsVerification(false); setResent(true); }
                catch (err) { setError(inlineError(err)); }
              })} className="block text-xs font-medium text-blue-600 hover:underline">
                {resend.busy ? t.common.loading : t.auth.resendLink}
              </button>
            )}
            {resent && <Notice tone="success" data-testid="resend-done">{t.auth.verificationResent}</Notice>}
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-xs text-gray-500 hover:text-gray-900">{t.home.forgotPasswordLink}</Link>
            </div>
            <button type="submit" disabled={signIn.busy} className="btn-primary w-full py-2.5" data-testid="login-submit">
              {signIn.busy ? t.common.loading : t.nav.login}
            </button>
          </form>
        </>
      )}

      <p className="text-center text-sm text-gray-500 mt-2">
        {t.home.noAccount}{' '}
        <Link to="/register" className="text-gray-900 font-medium hover:underline">{t.home.signUp}</Link>
      </p>

      {/* #529, #584 — the notices at collection: a first Google sign-in
          creates the account. They close the card, and each link starts
          its own line and never breaks. */}
      <div className="pt-4 border-t border-gray-100 space-y-3">
        <p data-testid="privacy-notice" className="text-xs text-gray-500 text-center">
          {t.home.privacyNotice}
          <span className="block mt-0.5">
            <Link to="/manual#privacy" className="underline whitespace-nowrap hover:text-gray-900">{t.home.privacyLink}</Link>
          </span>
        </p>
        <p data-testid="terms-notice" className="text-xs text-gray-500 text-center">
          {t.home.termsNotice}
          <span className="block mt-0.5">
            <Link to="/manual#terms" className="underline whitespace-nowrap hover:text-gray-900">{t.home.termsLink}</Link>
          </span>
        </p>
      </div>

    </AuthCard>
  );
}
